import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { arrayRemove, arrayUnion, addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from './firebase-config';
import { Link, useNavigate } from 'react-router-dom';
import './Trips.css';

const Icon = ({ name }) => {
  const paths = {
    search: <><circle cx="11" cy="11" r="6"/><path d="m16 16 4 4"/></>,
    map: <><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3Z"/><path d="M9 3v15M15 6v15"/></>,
    filter: <><path d="M4 6h16M7 12h10M10 18h4"/></>,
    mountain: <><path d="m3 19 6-11 3 5 2-3 7 9Z"/><path d="m7 12 2 2 2-2"/></>,
    calendar: <><path d="M5 4h14v16H5Z"/><path d="M8 2v4M16 2v4M5 9h14"/></>,
    users: <><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 19c0-4 2-6 6-6s6 2 6 6M15 14c3 0 5 2 5 5"/></>,
    pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2"/></>,
    arrow: <><path d="M5 12h14M14 7l5 5-5 5"/></>,
    plus: <><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></>,
    shield: <><path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6Z"/><path d="m9 12 2 2 4-5"/></>,
  };
  return <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};

const emptyTrip = { name: '', location: '', date: '', description: '' };

const formatDate = (date) => {
  if (!date) return 'Date flexible';
  const parsed = new Date(`${date}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

function Trips() {
  const [trips, setTrips] = useState([]);
  const [newTrip, setNewTrip] = useState(emptyTrip);
  const [editTrip, setEditTrip] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'trips'));
        setTrips(snapshot.docs.map((tripDoc) => ({ id: tripDoc.id, ...tripDoc.data() })));
      } catch (fetchError) {
        console.error(fetchError);
        setError('Unable to load expeditions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    const fetchUserProfile = async () => {
      if (!currentUser) return;
      try {
        const directSnapshot = await getDoc(doc(db, 'users', currentUser.uid));
        if (directSnapshot.exists()) {
          setCurrentUserProfile(directSnapshot.data());
          return;
        }
        const profileSnapshot = await getDocs(query(collection(db, 'users'), where('uid', '==', currentUser.uid)));
        if (!profileSnapshot.empty) setCurrentUserProfile(profileSnapshot.docs[0].data());
      } catch (profileError) {
        console.warn('Could not load current user profile.', profileError);
      }
    };

    fetchTrips();
    fetchUserProfile();
  }, [currentUser]);

  const regionOptions = useMemo(() => Array.from(new Set(trips.map((trip) => trip.location).filter(Boolean))).sort(), [trips]);
  const filteredTrips = useMemo(() => trips.filter((trip) => {
    const needle = search.trim().toLowerCase();
    const matchesSearch = !needle || [trip.name, trip.location, trip.description, trip.host].filter(Boolean).join(' ').toLowerCase().includes(needle);
    return matchesSearch && (region === 'all' || trip.location === region);
  }), [region, search, trips]);

  const createTrip = async () => {
    if (!currentUser) {
      navigate('/signup');
      return;
    }
    if (!currentUserProfile) {
      setError('Complete your profile before planning an expedition.');
      return;
    }
    if (!newTrip.name.trim() || !newTrip.location.trim()) {
      setError('Add an expedition name and location.');
      return;
    }

    const tripData = { ...newTrip, host: currentUserProfile.name || 'ClimberLink member', hostId: currentUser.uid, participants: [currentUser.uid] };
    try {
      const tripDoc = await addDoc(collection(db, 'trips'), tripData);
      setTrips((currentTrips) => [...currentTrips, { id: tripDoc.id, ...tripData }]);
      await updateDoc(doc(db, 'users', currentUser.uid), { trips: arrayUnion(tripDoc.id) });
      setNewTrip(emptyTrip);
      setShowCreateForm(false);
      setError('');
    } catch (createError) {
      console.error(createError);
      setError('Unable to create the expedition. Please try again later.');
    }
  };

  const joinTrip = async (tripId) => {
    if (!currentUser) {
      navigate('/signup');
      return;
    }
    try {
      await updateDoc(doc(db, 'trips', tripId), { participants: arrayUnion(currentUser.uid) });
      await updateDoc(doc(db, 'users', currentUser.uid), { trips: arrayUnion(tripId) });
      setTrips((currentTrips) => currentTrips.map((trip) => trip.id === tripId ? { ...trip, participants: Array.from(new Set([...(trip.participants || []), currentUser.uid])) } : trip));
      setError('');
    } catch (joinError) {
      console.error(joinError);
      setError('Unable to join the expedition. Please try again later.');
    }
  };

  const deleteTrip = async (tripId, hostId) => {
    if (currentUser?.uid !== hostId || !window.confirm('Delete this expedition? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'trips', tripId));
      await updateDoc(doc(db, 'users', currentUser.uid), { trips: arrayRemove(tripId) });
      setTrips((currentTrips) => currentTrips.filter((trip) => trip.id !== tripId));
    } catch (deleteError) {
      console.error(deleteError);
      setError('Unable to delete the expedition. Please try again later.');
    }
  };

  const saveEditTrip = async (tripId) => {
    try {
      await updateDoc(doc(db, 'trips', tripId), editTrip);
      setTrips((currentTrips) => currentTrips.map((trip) => trip.id === tripId ? { ...trip, ...editTrip } : trip));
      setEditTrip(null);
      setError('');
    } catch (editError) {
      console.error(editError);
      setError('Unable to save the expedition. Please try again later.');
    }
  };

  const updateNewTrip = (event) => setNewTrip((trip) => ({ ...trip, [event.target.name]: event.target.value }));
  const updateEditTrip = (event) => setEditTrip((trip) => ({ ...trip, [event.target.name]: event.target.value }));
  const openPlanner = () => currentUser ? setShowCreateForm(true) : navigate('/signup');

  return (
    <div className="expeditions-page">
      <header className="expeditions-hero">
        <div><span className="eyebrow">GLOBAL EXPEDITION HUB</span><h1>Find your next peak.</h1><p>Join a team, recruit the right partners, and turn the next objective into a shared plan.</p></div>
        <button className="plan-expedition-cta" onClick={openPlanner}><Icon name="plus" /> Plan expedition</button>
      </header>

      <section className="expedition-searchbar">
        <label><Icon name="search" /><span className="sr-only">Search expeditions</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search routes, peaks, or expedition hosts…" /></label>
        <label className="region-select"><Icon name="map" /><span className="sr-only">Filter by region</span><select value={region} onChange={(event) => setRegion(event.target.value)}><option value="all">All regions</option>{regionOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
        <button type="button" className="filter-button"><Icon name="filter" /> Filters</button>
      </section>

      {showCreateForm && (
        <section className="expedition-form panel">
          <div className="form-heading"><div><span className="eyebrow">NEW OBJECTIVE</span><h2>Plan an expedition</h2></div><button onClick={() => setShowCreateForm(false)} aria-label="Close planner">×</button></div>
          <div className="expedition-form-grid">
            <label><span>Expedition name</span><input name="name" value={newTrip.name} onChange={updateNewTrip} placeholder="e.g. Rainier summit push" /></label>
            <label><span>Location</span><input name="location" value={newTrip.location} onChange={updateNewTrip} placeholder="Mountain, range, or region" /></label>
            <label><span>Start date</span><input name="date" value={newTrip.date} onChange={updateNewTrip} type="date" /></label>
            <label className="description-field"><span>Expedition brief</span><textarea name="description" value={newTrip.description} onChange={updateNewTrip} placeholder="Route, experience required, pace, logistics…" /></label>
          </div>
          <div className="form-actions"><button className="secondary-action" onClick={() => setShowCreateForm(false)}>Cancel</button><button className="primary-action" onClick={createTrip}>Publish expedition</button></div>
        </section>
      )}

      {error && <div className="expedition-alert">{error}</div>}

      <div className="expeditions-body">
        <main>
          <div className="expedition-section-heading"><div><Icon name="mountain" /><div><span className="eyebrow">OPEN OBJECTIVES</span><h2>Active expeditions</h2></div></div><span>{filteredTrips.length} listed</span></div>
          {loading && <div className="expedition-empty">Loading expeditions…</div>}
          {!loading && filteredTrips.length === 0 && <div className="expedition-empty"><Icon name="compass" /><h3>No expeditions match this search</h3><p>Try another region or be the first to plan this objective.</p><button onClick={openPlanner}>Plan an expedition</button></div>}

          <div className="expedition-list">
            {filteredTrips.map((trip, index) => (
              <article className="expedition-card" key={trip.id}>
                <div className={`expedition-photo photo-${index % 3}`}><span>{trip.location || 'GLOBAL'}</span></div>
                <div className="expedition-card-content">
                  {editTrip?.id === trip.id ? (
                    <div className="inline-trip-editor">
                      <input name="name" value={editTrip.name || ''} onChange={updateEditTrip} placeholder="Trip name" />
                      <input name="location" value={editTrip.location || ''} onChange={updateEditTrip} placeholder="Location" />
                      <input name="date" value={editTrip.date || ''} onChange={updateEditTrip} type="date" />
                      <textarea name="description" value={editTrip.description || ''} onChange={updateEditTrip} placeholder="Description" />
                      <div><button className="secondary-action" onClick={() => setEditTrip(null)}>Cancel</button><button className="primary-action" onClick={() => saveEditTrip(trip.id)}>Save changes</button></div>
                    </div>
                  ) : (
                    <>
                      <div className="expedition-title"><div><span className="eyebrow">TEAM EXPEDITION</span><h3>{trip.name || 'Unnamed expedition'}</h3></div><span className="difficulty-pill">OPEN</span></div>
                      <p className="expedition-description">{trip.description || 'The expedition host has not added a full route brief yet.'}</p>
                      <dl className="expedition-facts">
                        <div><dt><Icon name="pin" /> LOCATION</dt><dd>{trip.location || 'TBD'}</dd></div>
                        <div><dt><Icon name="calendar" /> DEPARTURE</dt><dd>{formatDate(trip.date)}</dd></div>
                        <div><dt><Icon name="users" /> TEAM</dt><dd>{trip.participants?.length || 0} climbers</dd></div>
                      </dl>
                      <div className="expedition-card-footer">
                        <div><span>HOSTED BY</span><strong>{trip.host || 'ClimberLink member'}</strong></div>
                        <div className="trip-card-actions">
                          {currentUser?.uid === trip.hostId ? <><button className="text-action" onClick={() => setEditTrip(trip)}>Edit</button><button className="text-action danger" onClick={() => deleteTrip(trip.id, trip.hostId)}>Delete</button></> : <button className="join-action" onClick={() => joinTrip(trip.id)}>Request to join <Icon name="arrow" /></button>}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        </main>

        <aside className="expeditions-rail">
          <section className="map-card">
            <div><span>REGIONAL ACTIVITY</span><Icon name="map" /></div>
            <div className="map-visual"><Icon name="pin" /><strong>{trips.length} objectives</strong><span>across the network</span></div>
            <button onClick={() => setRegion('all')}>Explore all regions</button>
          </section>
          <section className="safety-card"><Icon name="shield" /><span className="eyebrow">PRO VERIFICATION</span><h2>Plan with confidence.</h2><p>Review climbing histories, credentials, and shared objectives before committing to a team.</p><Link to="/profiles">Browse verified climbers</Link></section>
          <section className="host-card"><h3>Lead the next objective</h3><p>Create a clear expedition brief and recruit from the ClimberLink network.</p><button onClick={openPlanner}>Start planning</button></section>
        </aside>
      </div>
    </div>
  );
}

export default Trips;
